import { endOfToday } from "date-fns/endOfToday";
import { endOfTomorrow } from "date-fns/endOfTomorrow";
import { endOfWeek } from "date-fns/endOfWeek";
import { startOfToday } from "date-fns/startOfToday";
import matches from "lodash/matches";
import pickBy from "lodash/pickBy";

/**
 * The due-date buckets the task list is grouped by. `completed` is the only one
 * holding done tasks and is never part of the default presentation.
 */
export type TaskDueBucket =
  | "overdue"
  | "today"
  | "tomorrow"
  | "this_week"
  | "later"
  | "no_due_date"
  | "completed";

/** The buckets shown when the list is not narrowed to a single one. */
export const DEFAULT_TASK_DUE_BUCKETS: TaskDueBucket[] = [
  "overdue",
  "today",
  "tomorrow",
  "this_week",
  "later",
  // Last, and its own bucket rather than folded into overdue: a task with no
  // date is unscheduled, not late. Without it these rows are invisible -- a
  // NULL satisfies no range predicate, so every other bucket's query skips them.
  "no_due_date",
];

/** The buckets the filter rail offers as a one-click shortcut. */
export const FILTERABLE_TASK_DUE_BUCKETS: TaskDueBucket[] = [
  "overdue",
  "today",
  "this_week",
  "later",
  "no_due_date",
  "completed",
];

/**
 * How "the task is done" is expressed in the query string. `done_date@is: null`
 * cannot survive a round trip through the URL — ra-core drops null filter
 * values (see its `removeEmpty`) — while a lower bound on a nullable timestamp
 * excludes the open tasks all the same, on both data providers.
 */
const EPOCH = new Date(0).toISOString();

export type TaskFilter = Record<string, unknown>;

/**
 * How a rail toggle writes "this column is null". The same `removeEmpty` that
 * costs the buckets their `done_date@is: null` would drop a real null here too,
 * so the toggle writes PostgREST's own wire literal for it — the string the
 * query string would have carried anyway.
 */
export const NULL_FILTER_VALUE = "null";

/**
 * Put the real nulls back before a query goes out. Both data providers want one
 * — PostgREST serialises it to the same `is.null`, and the FakeRest adapter
 * compares against it — and only a null test can carry the written form, so the
 * value is only reinterpreted under an `@is` / `@not.is` key. A task whose text
 * is the word "null" stays searchable.
 */
export const resolveNullFilterValues = (filter: TaskFilter): TaskFilter =>
  Object.fromEntries(
    Object.entries(filter).map(([key, value]) => [
      key,
      (key.endsWith("@is") || key.endsWith("@not.is")) &&
      value === NULL_FILTER_VALUE
        ? null
        : value,
    ]),
  );

/**
 * Boundaries are recomputed on every call rather than captured once: a list
 * left open overnight must not keep yesterday's idea of "today".
 */
const getDueDateBounds = (
  bucket: TaskDueBucket,
): { gte?: string; lt?: string } => {
  switch (bucket) {
    case "overdue":
      return { lt: startOfToday().toISOString() };
    case "today":
      return {
        gte: startOfToday().toISOString(),
        lt: endOfToday().toISOString(),
      };
    case "tomorrow":
      return {
        gte: endOfToday().toISOString(),
        lt: endOfTomorrow().toISOString(),
      };
    case "this_week":
      return {
        gte: endOfTomorrow().toISOString(),
        lt: endOfWeek(new Date(), { weekStartsOn: 0 }).toISOString(),
      };
    case "later":
      return { gte: endOfWeek(new Date(), { weekStartsOn: 0 }).toISOString() };
    case "no_due_date":
    case "completed":
      return {};
  }
};

/**
 * The filter a rail toggle writes. Every key any other bucket uses is listed,
 * `undefined` when this bucket does not need it, so switching bucket replaces
 * the whole range instead of intersecting it with the previous selection.
 */
export const getTaskDueBucketRailFilter = (
  bucket: TaskDueBucket,
): TaskFilter => {
  const { gte, lt } = getDueDateBounds(bucket);
  return {
    "due_date@gte": gte,
    "due_date@lt": lt,
    // Written as the wire literal, not a real null, for the same reason
    // done_date is -- see NULL_FILTER_VALUE.
    "due_date@is": bucket === "no_due_date" ? NULL_FILTER_VALUE : undefined,
    "done_date@gt": bucket === "completed" ? EPOCH : undefined,
  };
};

/** What one bucket's own query adds on top of the list-wide filter. */
export const getTaskDueBucketQueryFilter = (
  bucket: TaskDueBucket,
): TaskFilter => {
  const { gte, lt } = getDueDateBounds(bucket);
  return {
    ...(gte != null ? { "due_date@gte": gte } : {}),
    ...(lt != null ? { "due_date@lt": lt } : {}),
    ...(bucket === "no_due_date" ? { "due_date@is": null } : {}),
    ...(bucket === "completed"
      ? { "done_date@gt": EPOCH }
      : { "done_date@is": null }),
  };
};

/**
 * The bucket the rail currently narrows the list to, or null when the list is
 * showing the default grouping. Matched on the filter a toggle writes, so the
 * rail and the presentation can never disagree about what is selected.
 */
export const getSelectedTaskDueBucket = (
  filterValues: TaskFilter | undefined,
): TaskDueBucket | null =>
  FILTERABLE_TASK_DUE_BUCKETS.find((bucket) =>
    matches(
      pickBy(
        getTaskDueBucketRailFilter(bucket),
        (value) => value !== undefined,
      ),
    )(filterValues ?? {}),
  ) ?? null;
